import { createSlice } from '@reduxjs/toolkit';

const COMPLETION_SUMMARY_STORAGE_KEY = 'interview_completion_summary';

const loadStoredCompletionSummary = () => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = window.localStorage.getItem(COMPLETION_SUMMARY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Unable to read stored completion summary:', error);
    return null;
  }
};

const saveStoredCompletionSummary = (summary) => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    if (summary) {
      window.localStorage.setItem(COMPLETION_SUMMARY_STORAGE_KEY, JSON.stringify(summary));
    } else {
      window.localStorage.removeItem(COMPLETION_SUMMARY_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Unable to store completion summary:', error);
  }
};

const initialState = {
  // Session data from API
  sessionData: null,
  
  // Interview details
  interviewId: null,
  sessionId: null,
  candidateName: null,
  candidateEmail: null,
  jobTitle: null,
  companyName: null,
  companyLogo: null,
  expiresAt: null,
  startedAt: null,
  interviewType: [], // Allowed answer formats: ['video', 'audio', 'text']
  
  // Session timing
  sessionDeadline: null,
  sessionExpired: false,
  
  // Consent tracking
  consent: {
    termsAccepted: false,
    termsAcceptedAt: null,
    termsVersion: null,
    recordingConsent: false,
    recordingConsentAt: null,
    mediaMode: null,
    mediaModeSetAt: null,
  },
  
  // Current question
  currentQuestion: null,
  currentQuestionId: null,
  nextQuestionId: null,
  
  // All questions
  questions: [],
  
  // Progress tracking (from API)
  progress: {
    total_questions: 0,
    answered_questions: 0,
    completion_percentage: 0,
    is_started: false,
    is_completed: false,
    started_at: null,
    completed_at: null,
  },
  
  // Progress map for UI (1: "done", 2: "current", 3: "pending")
  progressMap: {},
  
  // Interview state
  isInterviewStarted: false,
  isInterviewComplete: false,
  
  // Answer format
  answerFormat: 'text', // 'text', 'audio', 'video'
  mediaPermissionGranted: false,
  
  // UI state
  loading: false,
  error: null,
  
  // Polling state
  isPolling: false,
  lastPollTime: null,
  
  // Media recording
  mediaRecording: {
    isRecording: false,
    recordedBlob: null,
    duration: 0,
  },
  
  // Response history
  responseHistory: [],

  // Completion summary from POST /complete
  completionSummary: loadStoredCompletionSummary(),

  // Async upload tracking
  uploadPending: false,
  pendingUploadResponseId: null,
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    // ========================================
    // Fetch Interview Summary (GET /api/public/interviews/:token)
    // ========================================
    fetchInterviewRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInterviewSuccess: (state, action) => {
      state.loading = false;
      const { interview } = action.payload || {};

      if (!interview) {
        console.error('[fetchInterviewSuccess] Missing interview in payload:', action.payload);
        return;
      }
      
      // Store raw session data
      state.sessionData = interview;
      
      // Map API fields to state
      state.interviewId = interview.interview_id;
      state.sessionId = interview.session_id;
      state.candidateName = interview.candidate_name;
      state.candidateEmail = interview.candidate_email;
      state.jobTitle = interview.job_title;
      state.companyName = interview.company_name;
      state.companyLogo = interview.company_logo;
      state.expiresAt = interview.expires_at;
      state.startedAt = interview.started_at;
      state.isInterviewStarted = interview.is_started || false;
      state.isInterviewComplete = interview.is_completed || false;
      state.interviewType = interview.interview_type || ['text']; // Allowed formats
      
      // Session timing
      state.sessionDeadline = interview.session_deadline;
      state.sessionExpired = interview.session_expired || false;
      
      // Consent data
      state.consent = {
        termsAccepted: interview.terms_accepted || false,
        termsAcceptedAt: interview.terms_accepted_at,
        termsVersion: interview.terms_version,
        recordingConsent: interview.recording_consent || false,
        recordingConsentAt: interview.recording_consent_at,
        mediaMode: interview.media_mode,
        mediaModeSetAt: interview.media_mode_set_at,
      };
      
      // Set answer format from consent
      if (interview.media_mode) {
        state.answerFormat = interview.media_mode;
        state.mediaPermissionGranted = interview.recording_consent || false;
      }
      
      // Progress data (from embedded progress object)
      if (interview.progress) {
        state.progress = {
          total_questions: interview.progress.total_questions || 0,
          answered_questions: interview.progress.answered_questions || 0,
          completion_percentage: interview.progress.completion_percentage || 0,
          is_started: interview.progress.is_started || false,
          is_completed: interview.progress.is_completed || false,
          started_at: interview.progress.started_at,
          completed_at: interview.progress.completed_at,
        };
        
        // 🔥 NEW: Also check progress.is_completed (backend auto-sets this)
        if (interview.progress.is_completed === true) {
          state.isInterviewComplete = true;
          console.log('🎉 Backend reports interview complete via progress.is_completed');
        }
      }
      
      // Question tracking
      state.currentQuestionId = interview.current_question_id;
      state.nextQuestionId = interview.next_question_id;
      
      // Progress map (for stepper UI)
      state.progressMap = interview.progress_map || {};
    },
    fetchInterviewFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // Start Interview (POST /api/public/interviews/:token/start)
    // ========================================
    startInterviewRequest: (state, action) => {
      state.loading = true;
      state.error = null;
      // Optimistically set consent from the request payload so InterviewSession
      // can show questions immediately after navigation, even if the API response
      // doesn't echo back all consent fields.
      if (action.payload) {
        const { mediaMode, termsAccepted, termsVersion, recordingConsent } = action.payload;
        if (mediaMode) {
          state.consent.mediaMode = mediaMode;
          state.answerFormat = mediaMode;
        }
        if (termsAccepted !== undefined) {
          state.consent.termsAccepted = termsAccepted;
        }
        if (termsVersion) {
          state.consent.termsVersion = termsVersion;
        }
        if (recordingConsent !== undefined) {
          state.consent.recordingConsent = recordingConsent;
        }
      }
    },
    startInterviewSuccess: (state, action) => {
      state.loading = false;
      const payload = action.payload || {};
      const interview = payload.interview || payload.session || payload;

      if (!interview || typeof interview !== 'object') {
        console.error('[startInterviewSuccess] Missing session/interview in payload:', action.payload);
        state.isInterviewStarted = true;
        return;
      }
      
      // Update with backend response
      state.sessionData = interview;
      state.isInterviewStarted = true;
      state.startedAt = interview.started_at || interview.start_time || state.startedAt;
      state.sessionDeadline = interview.session_deadline;
      state.progress.is_started = true;
      
      // Update consent from backend confirmation
      if (interview.terms_accepted !== undefined) {
        state.consent.termsAccepted = interview.terms_accepted;
        state.consent.termsAcceptedAt = interview.terms_accepted_at;
        state.consent.termsVersion = interview.terms_version;
      }
      
      if (interview.recording_consent !== undefined) {
        state.consent.recordingConsent = interview.recording_consent;
        state.consent.recordingConsentAt = interview.recording_consent_at;
      }
      
      if (interview.media_mode) {
        state.consent.mediaMode = interview.media_mode;
        state.consent.mediaModeSetAt = interview.media_mode_set_at;
        state.answerFormat = interview.media_mode;
      }
    },
    startInterviewFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // Fetch Questions (GET /api/public/interviews/:token/questions)
    // ========================================
    fetchQuestionsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchQuestionsSuccess: (state, action) => {
      state.loading = false;
      const { questions, current_question, progress, progress_map } = action.payload;
      
      state.questions = questions || [];
      
      // Update progress if provided
      if (progress) {
        state.progress = {
          total_questions: progress.total_questions || 0,
          answered_questions: progress.answered_questions || 0,
          completion_percentage: progress.completion_percentage || 0,
          is_started: progress.is_started || false,
          is_completed: progress.is_completed || false,
          started_at: progress.started_at,
          completed_at: progress.completed_at,
        };
        
        console.log('✅ Progress loaded:', state.progress);
      }
      
      // Update progress map
      if (progress_map) {
        state.progressMap = progress_map;
      }
      
      // 🔥 FIX: Handle current_question and detect completion
      if (current_question) {
        state.currentQuestion = current_question;
        state.currentQuestionId = current_question.id;
        console.log('📝 Current question loaded:', current_question.id);
      } else if (current_question === null) {
        // No current question
        state.currentQuestion = null;
        state.currentQuestionId = null;
        
        // 🔥 CHECK: If all answered (100% complete), mark interview as complete
        // Guard: only trigger if there are actually questions (avoids 0 >= 0 false-positive)
        if (state.progress.total_questions > 0 &&
            (state.progress.completion_percentage >= 100 || 
            state.progress.answered_questions >= state.progress.total_questions)) {
          state.isInterviewComplete = true;
          console.log('🎉 All questions answered - interview complete!');
        }
      }
    },
    fetchQuestionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // Poll Current Question (GET /api/public/interviews/:token/questions/current)
    // ========================================
    pollCurrentQuestionRequest: (state) => {
      state.isPolling = true;
      // Don't set loading = true for polling (background operation)
    },
    pollCurrentQuestionSuccess: (state, action) => {
      state.isPolling = false;
      state.lastPollTime = new Date().toISOString();
      
      const { current_question, session_expired, message } = action.payload;
      
      // Update session expiry status
      if (session_expired !== undefined) {
        state.sessionExpired = session_expired;
      }
      
      // Update current question if provided
      if (current_question) {
        state.currentQuestion = current_question;
        state.currentQuestionId = current_question.id;
      } else if (message === 'No more questions') {
        // All questions answered
        state.currentQuestion = null;
        state.currentQuestionId = null;
        state.isInterviewComplete = true; // 🔥 FIX: Use isInterviewComplete instead of progress.is_completed
        console.log('🎉 Polling detected: No more questions - interview complete!');
      } else if (session_expired) {
        // Session expired
        state.currentQuestion = null;
      }
    },
    pollCurrentQuestionFailure: (state, action) => {
      state.isPolling = false;
      // Don't set error for polling failures (silent background operation)
    },

    // ========================================
    // Submit Answer (POST /api/public/interviews/:token/answers)
    // ========================================
    submitAnswerRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    submitAnswerSuccess: (state, action) => {
      state.loading = false;
      const response = action.payload;
      
      console.log('🔄 submitAnswerSuccess payload:', response);
      
      // Add to response history
      if (response.answer?.id) {
        state.responseHistory.push({
          response_id: response.answer.id,
          question_id: response.answer.question_template_id,
          answer_text: response.answer.answer_text,
          created_at: response.answer.created_at || new Date().toISOString(),
          answered_late: response.answer.answered_late || false,
        });
      }
      
      // 🔥 UPDATE: Progress from response
      if (response.progress) {
        state.progress = {
          total_questions: response.progress.total_questions || 0,
          answered_questions: response.progress.answered_questions || 0,
          completion_percentage: response.progress.completion_percentage || 0,
          is_started: response.progress.is_started || false,
          is_completed: response.progress.is_completed || false, // 🔥 Backend now auto-sets this!
          started_at: response.progress.started_at,
          completed_at: response.progress.completed_at,
        };
        
        console.log('✅ Progress updated:', state.progress);
        
        // 🔥 NEW: Backend auto-completes when completion_percentage = 100
        if (response.progress.is_completed === true) {
          state.isInterviewComplete = true;
          console.log('🎉 Backend marked interview as complete!');
        }
      }
      
      // 🔥 NEW: Handle next_question from response
      if (response.next_question) {
        // Check if session expired
        if (response.next_question.session_expired) {
          state.sessionExpired = true;
          state.currentQuestion = null;
        } 
        // Check if current question exists (has more questions)
        else if (response.next_question.current_question) {
          state.currentQuestion = response.next_question.current_question;
          state.currentQuestionId = response.next_question.current_question.id;
          console.log('➡️ Next question loaded:', state.currentQuestion.id);
        } 
        // No more questions - backend should have set is_completed = true
        else if (response.next_question.current_question === null) {
          state.currentQuestion = null;
          state.currentQuestionId = null;
          // Double-check completion flag
          if (!state.isInterviewComplete) {
            state.isInterviewComplete = true;
            console.log('🎉 No more questions - marking complete!');
          }
        }
      }
      
        // Track async upload if pending
      if (response.upload_pending && response.answer?.id) {
        state.uploadPending = true;
        state.pendingUploadResponseId = response.answer.id;
      }

      // Clear media recording state
      state.mediaRecording = {
        isRecording: false,
        recordedBlob: null,
        duration: 0,
      };
    },
    submitAnswerFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // Complete Interview (POST /api/public/interviews/:token/complete)
    // ========================================
    completeInterviewRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    completeInterviewSuccess: (state, action) => {
      state.loading = false;
      state.isInterviewComplete = true;
      state.progress.is_completed = true;
      state.progress.completion_percentage = 100;
      state.progress.completed_at = new Date().toISOString();

      state.completionSummary = action.payload || null;
      saveStoredCompletionSummary(state.completionSummary);
      
      // Update session data if returned
      if (action.payload.interview) {
        state.sessionData = action.payload.interview;
      }
    },
    completeInterviewFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // Media Recording Actions
    // ========================================
    startRecording: (state) => {
      state.mediaRecording.isRecording = true;
      state.mediaRecording.recordedBlob = null;
      state.mediaRecording.duration = 0;
    },
    stopRecording: (state, action) => {
      state.mediaRecording.isRecording = false;
      if (action.payload) {
        state.mediaRecording.recordedBlob = action.payload.blob || null;
        state.mediaRecording.duration = action.payload.duration || 0;
      }
    },
    clearRecording: (state) => {
      state.mediaRecording = {
        isRecording: false,
        recordedBlob: null,
        duration: 0,
      };
    },

    // ========================================
    // Utility Actions
    // ========================================
    setMediaPermissionGranted: (state, action) => {
      state.mediaPermissionGranted = action.payload;
    },
    
    setCurrentQuestion: (state, action) => {
      state.currentQuestion = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },

    setUploadPending: (state, action) => {
      state.uploadPending = true;
      state.pendingUploadResponseId = action.payload;
    },
    clearUploadPending: (state) => {
      state.uploadPending = false;
      state.pendingUploadResponseId = null;
    },

    clearCompletionSummary: (state) => {
      state.completionSummary = null;
      saveStoredCompletionSummary(null);
    },

    setSessionExpired: (state, action) => {
      state.sessionExpired = action.payload;
    },
    
    resetInterviewState: () => {
      saveStoredCompletionSummary(null);
      return initialState;
    },
  },
});

// Export actions
export const {
  fetchInterviewRequest,
  fetchInterviewSuccess,
  fetchInterviewFailure,
  startInterviewRequest,
  startInterviewSuccess,
  startInterviewFailure,
  fetchQuestionsRequest,
  fetchQuestionsSuccess,
  fetchQuestionsFailure,
  pollCurrentQuestionRequest,
  pollCurrentQuestionSuccess,
  pollCurrentQuestionFailure,
  submitAnswerRequest,
  submitAnswerSuccess,
  submitAnswerFailure,
  completeInterviewRequest,
  completeInterviewSuccess,
  completeInterviewFailure,
  startRecording,
  stopRecording,
  clearRecording,
  setMediaPermissionGranted,
  setCurrentQuestion,
  clearError,
  setUploadPending,
  clearUploadPending,
  clearCompletionSummary,
  setSessionExpired,
  resetInterviewState,
} = interviewSlice.actions;

// Selectors
export const selectSessionData = (state) => state.interview.sessionData;
export const selectInterviewId = (state) => state.interview.interviewId;
export const selectCandidateName = (state) => state.interview.candidateName;
export const selectJobTitle = (state) => state.interview.jobTitle;
export const selectCompanyName = (state) => state.interview.companyName;
export const selectInterviewType = (state) => state.interview.interviewType; // Allowed formats
export const selectSessionDeadline = (state) => state.interview.sessionDeadline;
export const selectSessionExpired = (state) => state.interview.sessionExpired;
export const selectConsent = (state) => state.interview.consent;
export const selectCurrentQuestion = (state) => state.interview.currentQuestion;
export const selectCurrentQuestionId = (state) => state.interview.currentQuestionId;
export const selectNextQuestionId = (state) => state.interview.nextQuestionId;
export const selectQuestions = (state) => state.interview.questions;
export const selectProgress = (state) => state.interview.progress;
export const selectProgressMap = (state) => state.interview.progressMap;
export const selectIsInterviewStarted = (state) => state.interview.isInterviewStarted;
export const selectIsInterviewComplete = (state) => state.interview.isInterviewComplete;
export const selectCompletionSummary = (state) => state.interview.completionSummary;
export const selectLoading = (state) => state.interview.loading;
export const selectError = (state) => state.interview.error;
export const selectAnswerFormat = (state) => state.interview.answerFormat;
export const selectMediaPermissionGranted = (state) => state.interview.mediaPermissionGranted;
export const selectIsPolling = (state) => state.interview.isPolling;
export const selectLastPollTime = (state) => state.interview.lastPollTime;
export const selectUploadPending = (state) => state.interview.uploadPending;
export const selectPendingUploadResponseId = (state) => state.interview.pendingUploadResponseId;

export default interviewSlice.reducer;